import { useState, useMemo } from "react";
import { Button, Space, Switch, Table, Empty, Tooltip, message } from "antd";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  MailOutlined,
  CopyOutlined,
  UserOutlined,
  LineChartOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Typography } from "antd";
import { formatDate, formatNumber } from "../../../utils/formatters";
import Title from "antd/es/typography/Title";
import { colors } from "../../../config/constants";

/** Contributor metrics table (timeframe data); filter by selected date when set. */
function formatRefreshedAt(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return null;
  }
}

function ContributorMetrcisView({
  data = [],
  loading = false,
  isWeekly = false,
  startDate = null,
  endDate = null,
  lastRefreshedAt = null,
  onRefresh,
}) {
  const { Text } = Typography;
  const [hideZeroRows, setHideZeroRows] = useState(false);

  const refreshedLabel = formatRefreshedAt(lastRefreshedAt);

  const displayData = useMemo(() => {
    if (!hideZeroRows) return data;
    return data.filter(
      (row) => Number(row.total_hours) || Number(row.total_units),
    );
  }, [data, hideZeroRows]);

  const columns = useMemo(
    () => [
      {
        title: (
          <Space>
            <MailOutlined style={{ color: colors.baseBlue }} /> Contributor
            Email
          </Space>
        ),
        dataIndex: "email",
        key: "email",
        width: 160,
        ellipsis: true,
        className: "copy-icon-parent",
        render: (t, row) => (
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Text
              title={row.email}
              style={{
                width: "calc(100% - 1px)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "inline-block",
              }}
            >
              {t || "—"}
            </Text>
            <Tooltip title="Copy Email">
              <CopyOutlined
                onClick={() => {
                  navigator.clipboard.writeText(row.contributor_name);
                  message.success("Name copied to clipboard");
                }}
                style={{
                  cursor: "pointer",
                  color: colors.cyan7,
                  opacity: 0,
                }}
                className="copy-icon-show"
              />
            </Tooltip>
          </div>
        ),
        sorter: (a, b) => (a.email ?? "").localeCompare(b.email ?? ""),
      },
      {
        title: (
          <Space>
            <UserOutlined style={{ color: "#52c41a" }} /> Contributor Name
          </Space>
        ),
        dataIndex: "contributor_name",
        key: "contributor_name",
        width: 180,
        ellipsis: true,
        className: "copy-icon-parent",
        render: (t, row) => (
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Text
              title={row.contributor_name}
              style={{
                width: "calc(100%)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "inline-block",
              }}
            >
              {t || "—"}
            </Text>
            <Tooltip title="Copy Salesforce ID">
              <CopyOutlined
                onClick={() => {
                  navigator.clipboard.writeText(row.contributor_id);
                  message.success("Salesforce ID copied to clipboard");
                }}
                style={{
                  cursor: "pointer",
                  color: colors.cyan7,
                  opacity: 0,
                }}
                className="copy-icon-show"
              />
            </Tooltip>
          </div>
        ),
        sorter: (a, b) =>
          (a.contributor_id ?? "").localeCompare(b.contributor_id ?? ""),
      },
      {
        title: (
          <Space>
            <CalendarOutlined style={{ color: "#1890ff" }} /> Working Days
          </Space>
        ),
        dataIndex: "working_days",
        key: "working_days",
        width: 140,
        align: "right",
        hidden: !isWeekly,
        sorter: (a, b) => (a.working_days ?? 0) - (b.working_days ?? 0),
        render: (_, row) => (
          <Space orientation="vertical" size={0}>
            <Text>
              {row.working_days ?? 0} day
              {(row.working_days ?? 0) > 1 ? "s" : ""}
            </Text>
          </Space>
        ),
      },
      {
        title: (
          <Space>
            <ClockCircleOutlined style={{ color: "#fa8c16" }} />
            Delivered Hours
          </Space>
        ),
        dataIndex: "total_hours",
        key: "total_hours",
        width: 140,
        align: "right",
        sorter: (a, b) => (a.total_hours ?? 0) - (b.total_hours ?? 0),
        render: (_, row) => (
          <Space orientation="vertical" size={0}>
            <Text>{formatNumber(row.total_hours ?? 0, 0)} hours</Text>
          </Space>
        ),
      },
      {
        title: (
          <Space>
            <BarChartOutlined style={{ color: "#722ed1" }} />
            Delivered Units
          </Space>
        ),
        dataIndex: "total_units",
        key: "units",
        width: 140,
        align: "right",
        sorter: (a, b) => (a.total_units ?? 0) - (b.total_units ?? 0),
        render: (_, row) => (
          <Space orientation="vertical" size={0}>
            <Text>{formatNumber(row.total_units ?? 0, 0)} units</Text>
          </Space>
        ),
      },
      {
        title: (
          <Space>
            <LineChartOutlined style={{ color: "#13c2c2" }} />
            {isWeekly ? "Average RPH" : "Daily RPH"}
          </Space>
        ),
        dataIndex: isWeekly ? "average_rph" : "daily_rph",
        key: isWeekly ? "average_rph" : "daily_rph",
        width: 90,
        align: "right",
        sorter: (a, b) =>
          (a[isWeekly ? "average_rph" : "daily_rph"] ?? 0) -
          (b[isWeekly ? "average_rph" : "daily_rph"] ?? 0),
        render: (v) => (v != null ? formatNumber(v, 0) : "—"),
      },
    ],
    [isWeekly],
  );

  return (
    <div
      style={{
        margin: "24px",
        border: "1px solid #e8e8e8",
        borderRadius: 8,
        backgroundColor: "white",
        padding: "0 24px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          margin: "12px 0",
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "row",
            gap: 8,
          }}
        >
          <Title level={5}>
            Contributor {isWeekly ? "Weekly" : "Daily"} Metrics
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {isWeekly
              ? `(${formatDate(startDate)} - ${formatDate(endDate)})`
              : `${formatDate(startDate)}`}
          </Text>
        </span>
        <Space align="center" wrap>
          {onRefresh && (
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={onRefresh}
              loading={loading}
            >
              Refresh
            </Button>
          )}
          {refreshedLabel && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Last refreshed: {refreshedLabel}
            </Text>
          )}
          <Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Hide 0 hrs & 0 units
            </Text>
            <Switch
              size="small"
              checked={hideZeroRows}
              onChange={setHideZeroRows}
            />
          </Space>
        </Space>
      </div>
      <Table
        loading={loading}
        columns={columns}
        dataSource={displayData}
        rowKey={(r) =>
          `${r.week_ending_date}-${r.contributor_id ?? r.contributor_project_id ?? Math.random()}`
        }
        size="small"
        sticky
        bordered
        pagination={{
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "30", "50"],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} row${total !== 1 ? "s" : ""}${hideZeroRows ? " (0 hrs/units hidden)" : ""}`,
        }}
        scroll={{ x: 800, y: 260 }}
        locale={{
          emptyText: (
            <Empty
              description={
                data.length === 0
                  ? "No contributor metrics for selected date/week."
                  : hideZeroRows && displayData.length === 0
                    ? "No rows with hours or units. Turn off 'Hide 0 hrs & 0 units' to see all."
                    : "No rows for selected date/week."
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
        rowClassName={(index) =>
          index % 2 === 0 ? "table-row-light" : "table-row-dark"
        }
      />
      <style>{`
        .copy-icon-parent:hover .copy-icon-show {
        opacity: 1 !important;
        transition: opacity 0.2s ease-in-out;
        }
      `}</style>
    </div>
  );
}

export default ContributorMetrcisView;
